import React, { useState, useEffect } from "react";
import moment from "moment";

import { ApiCallResult, useAPI } from "services/Api";
import Input from "input/Input";
import Page from "pages/Page";
import { navigate } from "utility/NavigationService";
import { renderLoader } from "utility/LoaderUtils";
import { useNotificationManager } from "notifications/NotificationManager";

import * as R from "reports/Report"

const ReportReview = ({ componentName = "[ReportReview]" }) => {
    const [filter, setFilter] = useState(null);
    const [filteredOutcomeReports, setFilteredOutcomeReports] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState("Retrieving Outcome Reports");
    const [outcomeReports, setOutcomeReports] = useState(null);

    const API = useAPI();
    const notificationManager = useNotificationManager();

    // ---=== Utilities ===--- //

    const goToReportReview = (report) => {
        void navigate(`/outcomes/${report.id}/${report.status === "Not Submitted"}`);
    };

    // ---=== Report Operations ===--- //

    const api_GetReports = async () => {
        setIsLoading(true);
        setLoadingMessage("Retrieving Outcome Reports");

        const result: ApiCallResult = await API.api_Get(
            componentName,
            `/outcome_reports/?inreview=true`,
            notificationManager,
            (response) => {
                let reports = response.data.results;
                if (reports?.length > 0) {
                    reports = reports.sort(
                        (a, b) => {
                            return moment(a.prepared_date).isBefore(moment(b.prepared_date)) ? 1 : -1;
                        }
                    );

                    setOutcomeReports(reports);
                }
            },
            (error) => {
                console.error(error);
                notificationManager.addError(error);
                setOutcomeReports([]);
            }
        );

        setIsLoading(!(result.wasSuccessful && result.wasCallable));
    };

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            void api_GetReports();
        },
        []
    );

    useEffect(() => {
        if (outcomeReports?.length > 0) {
            let reports = outcomeReports;

            if (filter) {
                let f = filter.trim().toLowerCase();
                reports = reports.filter(r => r.patient_name.toLowerCase().includes(f));
            }

            reports = reports.sort(
                (a, b) => {
                    return moment(a.prepared_date).isBefore(moment(b.prepared_date)) ? 1 : -1;
                }
            );

            setFilteredOutcomeReports(reports);
        }
    }, [outcomeReports, filter]);

    // ---=== Render ===--- //

    const getModularHeaderConfig = () => {
        return {
            trail: breadCrumbLinks()
        };
    };

    const breadCrumbLinks = () => {
        return [
            { label: "Reports", url: "/reports" }
        ];
    };

    const renderHeader = () => {
        return (
            <div className="w-full h-[10%] z-40 relative px-6 pt-5 pb-[19px] justify-start items-center gap-4 inline-flex">
                <div className="w-[50%] h-full  justify-start items-center gap-2 flex">
                    <div className="text-gray-900 text-lg font-medium leading-7">
                        Reports for Review
                    </div>
                    <div className="mix-blend-multiply justify-start items-start flex">
                        <div className="px-2 py-0.5 bg-slate-50 rounded-2xl justify-center items-center flex">
                            <div className="text-center text-blue-800 text-xs font-medium leading-[18px]">{filteredOutcomeReports?.length ?? 0} ready</div>
                        </div>
                    </div>
                </div>
                <div className="grow shrink basis-0 h-5 px-[25px] justify-end items-center gap-2 flex"/>
                <div className="w-[25%] flex-col justify-start items-start inline-flex">
                    {
                        (filteredOutcomeReports?.length > 0) && (
                            <div className="self-stretch px-3.5 py-1.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex">
                                <div className="grow shrink basis-0 h-full justify-start items-center gap-2 flex">
                                    <img className="w-6 h-6 relative" src="/icons/search.svg" alt="search"/>
                                    <Input
                                        className="outline-none border-0 focus:outline-none	active:border-0 focus:border-0 focus:ring-0 overflow-hidden w-full grow shrink basis-0 h-6 text-gray-500 text-base font-normal leading-normal"
                                        name="Search Names"
                                        overrideText={"Search Names"}
                                        required
                                        type="text"
                                        value={filter ? filter : ""}
                                        // Callbacks
                                        onChange={setFilter}
                                    />
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
        );
    };

    const renderTableRows = () => {
        let tableRows = [];

        if (filteredOutcomeReports?.length > 0) {
            filteredOutcomeReports.forEach(
                (report) => {
                    let reportWeek = report.report_week || report.sample_plan_weeks;

                    tableRows.push(
                        <React.Fragment key={`outcome-report-${report.id}`}>
                            <tr id={`outcome-report-${report.id}`} className="whitespace-nowrap py-3 text-xs font-semibold even:bg-white odd:bg-gray-100 border-x-0 border-y-2 border-gray-200">
                                <td className="w-auto px-4 text-left   text-gray-900">{report.patient_name ? report.patient_name : "[UNKNOWN]"}</td>
                                <td className="w-auto px-4 text-center text-gray-500 ">{R.isMonth6Report(reportWeek) ? "6 Months Post" : `Week ${reportWeek}`}</td>
                                <td className="w-auto px-4 text-center text-gray-500">{moment(report.prepared_date).format("MMM D, YYYY")}</td>
                                <td className="w-20 px-6 h-[58px] border-gray-300 border-r-2">
                                    <button
                                        className="inline-flex items-center justify-center whitespace-nowrap min-w-[110px] px-4 h-[32px] gap-2 bg-brand-600 hover:bg-brand-700 border-brand-600 rounded-lg shadow border cursor-pointer"
                                        onClick={_e => goToReportReview(report)}
                                    >
                                        <img className="relative" src="/icons/file-search-02.svg" alt="icon"/>
                                        <div className="text-white text-sm font-semibold leading-tight">Review</div>
                                    </button>
                                </td>
                                <td className="w-auto px-4 text-left   text-gray-500 ">{report.coach_name}</td>
                                <td className="w-auto px-4 text-left   text-gray-400 ">{report.physician_name}</td>
                                <td className="w-auto px-4 text-left   text-gray-400 ">{report.third_party_administrators}</td>
                                <td className="w-auto px-4 text-left   text-gray-400 ">{report.insurers}</td>
                            </tr>
                        </React.Fragment>
                    );
                }
            );
        }

        return tableRows;
    };

    const renderTable = () => {
        return (
            <div className="grid grid-col w-full whitespace-nowrap overflow-y-auto">
                <table className="table-auto w-full border-collapse">
                    <thead className="whitespace-nowrap	w-full">
                        <tr className="whitespace-nowrap sticky shadow-[inset_0_1px_0_#d3d3d3,inset_0_-1px_0_#d3d3d3] h-[58px] py-3 bg-gray-50 text-gray-500 border-gray-200 border-x-0 border-y-2">
                            <th scope="col" className="w-auto px-6 text-left   text-xs font-medium text-gray-500">Patient</th>
                            <th scope="col" className="w-auto px-4 text-center text-xs font-medium text-gray-500">Report Week</th>
                            <th scope="col" className="w-auto px-4 text-center text-xs font-medium text-gray-500">Created</th>
                            <th scope="col" className="w-auto px-6 text-center text-xs font-medium text-gray-500">Actions</th>
                            <th scope="col" className="w-auto px-8 text-center text-xs font-medium text-gray-500">Coach</th>
                            <th scope="col" className="w-auto px-4 text-left   text-xs font-medium text-gray-500">Physician</th>
                            <th scope="col" className="w-auto px-4 text-left   text-xs font-medium text-gray-500">TPA</th>
                            <th scope="col" className="w-auto px-4 text-left   text-xs font-medium text-gray-500">Insurer</th>
                        </tr>
                    </thead>
                    <tbody className="whitespace-nowrap">
                        {renderTableRows()}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderNoReportsAvailable = () => {
        return (
            <div className="grid grid-col w-full whitespace-nowrap overflow-y-auto">
            </div>
        );
    };

    return (
        <Page
            componentName={componentName}
            headerConfig={getModularHeaderConfig()}
            internal={true}
        >
            <div className="w-full h-full flex items-start mt-24">
                {
                    isLoading
                        ? (
                            renderLoader(loadingMessage)
                        )
                        : (
                            <div className="flex flex-row">
                                <div id="table-margin-left-fixed" className={"w-20 flex-shrink-0"}/>
                                <div id="table-container-stretchy" className="w-full flex flex-grow items-start justify-center whitespace-nowrap overflow-hidden">
                                    <div id="table-container" className={"w-fit min-w-[1000px] flex flex-col overflow-hidden bg-white border-2 border-gray-200 rounded-lg shadow-lg"}>
                                        {renderHeader()}
                                        {filteredOutcomeReports?.length > 0 ? renderTable() : renderNoReportsAvailable()}
                                    </div>
                                </div>
                                <div id="table-margin-right-shrink" className={"w-20 flex-shrink-0"}/>
                            </div>
                        )
                }
            </div>
        </Page>
    );
};

export default ReportReview;
